/**
 * Ingredient Photo Photography Styles & Prompt Builder for Admin CRM
 * Generates minimalist, distraction-free reference photography prompts for Gemini
 * image generation, specifically designed for user ingredient recognition.
 */

export const INGREDIENT_CATEGORY_GUIDANCE = {
  vegetables: "Whole fresh specimen placed naturally, if applicable showing a single clean cut cross-section next to it so the inside texture and color are clearly identifiable.",
  produce: "Whole fresh specimen placed naturally, if applicable showing a single clean cut cross-section next to it so the inside texture and color are clearly identifiable.",
  fruits: "Whole fresh fruit placed naturally, if applicable showing a single clean slice or cross-section next to it so the inside texture and seed/flesh structure are clearly identifiable.",
  fruit: "Whole fresh fruit placed naturally, if applicable showing a single clean slice or cross-section next to it so the inside texture and seed/flesh structure are clearly identifiable.",
  herbs: "A single fresh vibrant sprig or small neat bunch with distinct leaf shapes clearly visible.",
  spices: "A small, neat pinch or mound placed in a small shallow clear glass pinch bowl or directly on the surface with visible ground texture.",
  seasonings: "A small, neat pinch or mound placed in a small shallow clear glass pinch bowl or directly on the surface with visible ground texture.",
  oils_vinegars: "A small clear glass ramekin or bowl showing liquid transparency, natural viscosity, and true color.",
  liquids: "A small clear glass ramekin or bowl showing liquid transparency, natural viscosity, and true color.",
  meat: "Single fresh, raw cut or portion displayed cleanly on the surface showing meat grain and fresh marbling.",
  poultry: "Single fresh, raw cut or portion displayed cleanly on the surface showing meat grain and fresh texture.",
  seafood: "Single fresh, raw fillet or portion displayed cleanly on the surface showing natural grain and fresh sheen.",
  dairy: "A neat block, wedge, or portion with clean texture visible.",
  cheese: "A neat block, wedge, or portion with clean texture visible.",
  bakery: "A single piece or loaf with clean crust and crumb texture visible.",
  grains_pasta: "A neat small mound or clear glass bowl of raw dry grains/pasta showing individual kernel or grain shape.",
  pantry: "Single raw unprocessed ingredient in its natural state, clean and clearly recognizable."
};

export const INGREDIENT_NEGATIVE_CONSTRAINTS =
  "No cooking pots, pans, utensils, cutting boards, knives, plates, dishes, bowls (except clear glass pinch bowl when required for powders/liquids), hands, human fingers, packaging, labels, barcodes, plastic wrap, cans, jars, grocery carts, text, watermarks, steam, cooked food, sauces, or finished meals.";

/**
 * Resolves state guidance text based on category or subgroup code/name.
 * @param {string} category
 * @returns {string}
 */
export function getStateGuidanceForCategory(category) {
  if (!category) return "Single raw fresh ingredient displayed whole, clean, and clearly recognizable.";
  const key = String(category).toLowerCase().trim().replace(/[^a-z0-9_]/g, "_");

  for (const [catPattern, guidance] of Object.entries(INGREDIENT_CATEGORY_GUIDANCE)) {
    if (key.includes(catPattern) || catPattern.includes(key)) {
      return guidance;
    }
  }

  return "Single raw fresh ingredient displayed whole, clean, and clearly recognizable.";
}

/**
 * Builds an ingredient reference photo prompt for Gemini image generation.
 *
 * @param {string} displayName The name of the ingredient (e.g. "Roma Tomato", "Fresh Basil")
 * @param {string} [category=""] Category code or display name (e.g. "produce", "spices")
 * @param {object} [options={}] Additional overrides
 * @returns {{ promptText: string, category: string, stateGuidance: string }}
 */
export function buildIngredientPhotoPrompt(displayName, category = "", options = {}) {
  const cleanName = String(displayName || "").trim();
  const stateGuidance = options.customGuidance || getStateGuidanceForCategory(category);

  const promptText = `Clean food ingredient identification shot of raw, fresh ${cleanName}.
Composition: Centered single subject, clean top-down 45-degree angle showing full shape and distinguishing characteristics, on a seamless neutral light off-white surface.
State: ${stateGuidance}
Lighting: Soft natural daylight from side window, gentle soft shadows, crisp sharp focus, natural true-to-life colors.
Style: Professional culinary reference photography, high resolution, minimalist, uncluttered.
Negative constraints: ${INGREDIENT_NEGATIVE_CONSTRAINTS}`.trim();

  return {
    promptText,
    category,
    stateGuidance
  };
}
